import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ChargerModule from '../../../components/chargerModule';
import { IlogisticServiceDetails } from '../../../types/services';
import { getLogisticServiceDetails, retrieveLogisticServiceHistory } from '../utils/logisticServices.utils';
import { IlogisticServiceDetailsTab } from './Components/LogisticServiceDetailsHeader/LogisticServiceDetailsTabs/LogisticServiceDetailsTabs';
import LogisticServiceDetailsHeader from './Components/LogisticServiceDetailsHeader/LogisticServiceDetailsHeader';
import LogisticServiceDetailsTabSkeletonLoader from './Components/LogisticServiceDetailsTabContentSkeletonLoader/LogisticServiceDetailsTabContentSkeletonLoader';
import useServiceState from '../../../state/useServicesState';
import { ILogisticServiceHistory } from '../../../types/suscription/history.types';
import { buildCompleteLogisticServiceHistory } from '../../../utils/history';
import useCommentsState from '../../../state/useCommentsState';

import './LogisticServiceDetailsView.scss';

const LogisticServiceDetailsView = () => {
  const { logisticServiceId } = useParams<{ logisticServiceId: string }>();
  const { setDestinationDataChangedCheck, setOriginDataChangedCheck, resetDataInLSDetails, resetDataWithoutLoader } =
    useServiceState();
  const { clearComments, setComments } = useCommentsState();
  const [currentTabData, setCurrentTabData] = useState<IlogisticServiceDetailsTab>();
  const [logisticServiceData, setLogisticServiceData] = useState<IlogisticServiceDetails>();
  const [loadingLogisticServiceData, setLoadingLogisticServiceData] = useState(false);
  const [historyData, setHistoryData] = useState<ILogisticServiceHistory[]>([]);
  const [shouldCombineHistory, setShouldCombineHistory] = useState<boolean>(true);
  const [loadingLogisticServiceHistoryData, setLoadingLogisticServiceHistoryData] = useState(false);
  const isFirstRender = useRef(true);

  const infoSideBar = { position: 'logisticService' };
  const headerInfo = { title: 'Servicio logístico', subtitle: 'Detalle' };

  const getLsData = async (noLoading?: boolean) => {
    await getLogisticServiceDetails(
      logisticServiceId,
      setLogisticServiceData,
      noLoading ? undefined : setLoadingLogisticServiceData
    );
  };

  const getLsHistoryData = async (noLoading?: boolean) => {
    await retrieveLogisticServiceHistory(
      logisticServiceId,
      setHistoryData,
      setShouldCombineHistory,
      noLoading ? undefined : setLoadingLogisticServiceHistoryData
    );
  };

  const updateDataCallback = async (noLoading = false) => {
    await getLsData(noLoading);
    await getLsHistoryData(noLoading);
  };

  const syncCommentsWithStore = () => {
    if (logisticServiceData?.logisticComments) {
      setComments(logisticServiceData.logisticComments);
    }
  };

  useEffect(() => {
    return () => {
      setDestinationDataChangedCheck(false);
      setOriginDataChangedCheck(false);
      clearComments();
    };
  }, [clearComments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isFirstRender.current) return;
    updateDataCallback(true);
  }, [resetDataWithoutLoader]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isFirstRender.current) return;
    updateDataCallback();
  }, [resetDataInLSDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    isFirstRender.current = false;
    updateDataCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!logisticServiceData || !shouldCombineHistory) return;
    setHistoryData(buildCompleteLogisticServiceHistory(logisticServiceData, historyData));
    setShouldCombineHistory(false);
  }, [logisticServiceData, historyData, shouldCombineHistory]);

  useEffect(() => {
    syncCommentsWithStore();
  }, [logisticServiceData?.logisticComments]);

  return (
    <ChargerModule infoSideBar={infoSideBar} headerInfo={headerInfo}>
      <div className="LogisticServiceDetailsView">
        <LogisticServiceDetailsHeader
          logisticServiceData={logisticServiceData || {}}
          logisticServiceHistoryData={historyData}
          handleTabSelectionCallback={setCurrentTabData}
          showSkeletonLoader={loadingLogisticServiceData || loadingLogisticServiceHistoryData}
          updateDataCallback={updateDataCallback}
          updateWithoutLoader={() => updateDataCallback(true)}
        />
        {!loadingLogisticServiceData && !loadingLogisticServiceHistoryData && currentTabData?.tabComponent}
        {(loadingLogisticServiceData || loadingLogisticServiceHistoryData) && (
          <LogisticServiceDetailsTabSkeletonLoader />
        )}
      </div>
    </ChargerModule>
  );
};

export default LogisticServiceDetailsView;
